import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { sendPageView } from "@/lib/gaPageView";
import { cioPage } from "@/lib/customerio";

/**
 * Fires a GA4 `page_view` and a Customer.io `page` on every resolved route.
 *
 * The gtag config uses `send_page_view: false`, so this hook is the single
 * source of pageviews. A short delay lets React Router settle redirects
 * (e.g. `/` -> `/en-US/`) and lets the page set its title before we read it.
 *
 * Customer.io shares the same effect and the same path dedupe on purpose: the
 * initial route and every route change produce exactly one page call per
 * destination, with no second emitter to double count.
 */
export function useGAPageView(): void {
  const location = useLocation();
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    const path = `${location.pathname}${location.search}`;
    if (lastPath.current === path) return;
    lastPath.current = path;

    // 350ms: long enough for redirect chains to cancel via cleanup and for
    // SEOHead to apply the new document.title before we read it.
    const timer = window.setTimeout(() => {
      sendPageView();
      cioPage();
    }, 350);
    return () => window.clearTimeout(timer);
  }, [location.pathname, location.search]);
}

/** Mountable wrapper so the hook can live inside <BrowserRouter>. */
const GAPageViewTracker = () => {
  useGAPageView();
  return null;
};

export default GAPageViewTracker;
